import { MutableRefObject } from 'react';
import { School } from '@common-types';
import { CoreBox, CoreTypography } from '@youscience/core';

import { moneyFormatter } from '@utils/formatter';

import {
  sxStyles,
  StyledStudentOutcomesWrap,
  StyledStudentOutcomesInner,
  StyledFilledRate,
} from './StudentOutcomes.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { linkOffset, SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

interface StudentOutcomesProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  school: School;
  hasData: boolean;
}

export const StudentOutcomes = (props: StudentOutcomesProps) => {
  const {
    scrollToRef,
    school: { collegeScorecard },
    hasData,
  } = props;

  const retentionRate = collegeScorecard?.latest?.student?.retention_rate?.four_year?.full_time;
  const completionRate = collegeScorecard?.latest?.completion?.completion_rate_4yr_150nt_pooled;
  const earningsCost = collegeScorecard?.latest?.earnings?.['6_yrs_after_entry']?.median;

  const isHaveRates = retentionRate || completionRate;

  const renderOutcomesItem = (desc: string, title: string, rate?: number) => {
    if (!rate) return '';

    const truncRateValue = Math.trunc(rate * 100 * 100) / 100;

    return (
      <CoreBox sx={sxStyles.outcomesItemWrap}>
        <CoreTypography variant="body2">{desc}</CoreTypography>

        <CoreTypography variant="h6" component="h5">
          {title}
        </CoreTypography>

        <CoreBox sx={sxStyles.rateWrap}>
          <CoreBox sx={sxStyles.rateInner}>
            <StyledFilledRate filledRate={truncRateValue} />
          </CoreBox>

          <CoreTypography component="div" variant="body2">
            {`${truncRateValue}%`}
          </CoreTypography>
        </CoreBox>
      </CoreBox>
    );
  };

  if (!hasData) return null;

  return (
    <CoreBox sx={sxStyles.studentOutcomesContainer}>
      <SectionLinkOffset id={PageSectionIds['students-outcomes']} ref={scrollToRef} sx={linkOffset(300)} />

      <CoreTypography sx={sxStyles.studentOutcomesSectionTitle} variant="h4" component="h3">
        Student outcomes
      </CoreTypography>

      <StyledStudentOutcomesWrap>
        <CoreTypography sx={sxStyles.studentOutcomesSubtitle} variant="h5" component="h4">
          Retention, completion, and earnings
        </CoreTypography>

        <StyledStudentOutcomesInner>
          {isHaveRates && (
            <CoreBox>
              <CoreTypography sx={sxStyles.outcomesTitle} variant="overline" component="h5">
                Outcomes
              </CoreTypography>

              {renderOutcomesItem(
                'The rate of how many students return after a year',
                'Retention rate',
                retentionRate || 0,
              )}

              {renderOutcomesItem('The rate of program completion', 'Completion rate', completionRate || 0)}
            </CoreBox>
          )}

          {earningsCost && (
            <CoreBox sx={sxStyles.earnings}>
              <CoreTypography component="div" variant="body2">
                For graduates
              </CoreTypography>

              <CoreTypography component="div" variant="h6">
                Median earnings for graduates
              </CoreTypography>

              <CoreTypography sx={sxStyles.earningsCost} variant="h3" component="h2">
                {moneyFormatter.format(earningsCost)}
              </CoreTypography>
            </CoreBox>
          )}
        </StyledStudentOutcomesInner>
      </StyledStudentOutcomesWrap>
    </CoreBox>
  );
};
